/**
 * LmsLearnCourse — user reads lessons + takes quizzes.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ChevronRight, PlayCircle, HelpCircle, Award, Download } from "lucide-react";

function ytEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?]+)/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  if (url.includes("loom.com/share/")) return url.replace("/share/", "/embed/");
  return url;
}

function renderMd(md) {
  // Minimal markdown-ish rendering — headings, bullets, bold, code
  if (!md) return null;
  const lines = md.split("\n");
  return lines.map((l, i) => {
    if (l.startsWith("### ")) return <h3 key={i} className="text-base font-semibold mt-4 mb-2">{l.slice(4)}</h3>;
    if (l.startsWith("## ")) return <h2 key={i} className="text-lg font-semibold mt-4 mb-2">{l.slice(3)}</h2>;
    if (l.startsWith("# ")) return <h1 key={i} className="text-xl font-bold mt-4 mb-2">{l.slice(2)}</h1>;
    if (l.startsWith("- ") || l.startsWith("* ")) return <li key={i} className="ml-6 list-disc">{l.slice(2)}</li>;
    if (l.startsWith("```")) return null;
    if (l.trim() === "") return <div key={i} className="h-2" />;
    // bold
    const html = l.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/`([^`]+)`/g, '<code class="bg-muted px-1 text-xs">$1</code>');
    return <p key={i} className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

export default function LmsLearnCourse() {
  const { courseId } = useParams();
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/lms/me/courses/${courseId}`);
      setCourse(data);
      // pick first incomplete lesson
      const firstOpen = (data.lessons || []).find((l) => !l.completed) || data.lessons?.[0];
      setCurrentLesson(firstOpen);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [activeWorkspaceId, courseId]);
  useEffect(() => { load(); }, [load]);

  const complete = async (lessonId) => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/lms/lessons/${lessonId}/complete`);
      await load();
      toast.success("Lesson marked complete");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const downloadCert = async () => {
    try {
      const res = await api.get(`/workspaces/${activeWorkspaceId}/lms/courses/${courseId}/certificate`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${course.title}-certificate.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Certificate not ready")); }
  };

  if (!course) return <div className="p-6">Loading…</div>;
  const isCompleted = course.my_enrollment?.status === "completed";

  return (
    <div className="p-6 grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4">
      <aside className="border border-border p-3 space-y-3 max-h-[calc(100vh-100px)] overflow-y-auto">
        <div>
          <h2 className="text-lg font-semibold">{course.title}</h2>
          <div className="text-[10px] text-muted-foreground uppercase mt-0.5">{course.category}</div>
        </div>
        <div>
          <div className="w-full h-2 bg-muted rounded-none">
            <div className="h-2 bg-emerald-500" style={{ width: `${course.my_enrollment?.progress_percent || 0}%` }} />
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">{course.my_enrollment?.progress_percent || 0}% complete</div>
        </div>
        {isCompleted && course.cert_enabled && (
          <Button onClick={downloadCert} className="w-full rounded-none h-8" data-testid="lms-cert-download">
            <Award className="w-3.5 h-3.5 mr-1" /> Download certificate
          </Button>
        )}
        <div className="space-y-2">
          {(course.modules || []).map((m) => (
            <div key={m.id}>
              <div className="text-[10px] uppercase font-semibold tracking-widest text-muted-foreground mb-1">{m.title}</div>
              {(course.lessons || []).filter((l) => l.module_id === m.id).map((l) => (
                <button key={l.id} onClick={() => setCurrentLesson(l)} className={`w-full text-left flex items-center gap-2 p-2 text-xs hover:bg-muted/40 ${currentLesson?.id === l.id ? "bg-primary/10 border-l-2 border-primary" : ""}`} data-testid={`lms-lesson-nav-${l.id}`}>
                  {l.completed ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> : <PlayCircle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                  <span className="flex-1 truncate">{l.title}</span>
                </button>
              ))}
            </div>
          ))}
          {(course.quizzes || []).length > 0 && (
            <div className="border-t border-border pt-2 mt-3">
              <div className="text-[10px] uppercase font-semibold tracking-widest text-muted-foreground mb-1">Assessments</div>
              {(course.quizzes || []).map((q) => (
                <button key={q.id} onClick={() => navigate(`/app/lms/quizzes/${q.id}/take?enrollment_id=${course.my_enrollment?.id}`)} className="w-full text-left flex items-center gap-2 p-2 text-xs hover:bg-muted/40" disabled={q.my_attempt_count >= q.max_attempts} data-testid={`lms-quiz-take-${q.id}`}>
                  <HelpCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  <span className="flex-1 truncate">{q.title}</span>
                  {q.my_latest_attempt?.pass != null && (
                    <span className={`chip text-[9px] ${q.my_latest_attempt.pass ? "chip-success" : "chip-danger"}`}>{q.my_latest_attempt.score_percent}%</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </aside>

      <main className="border border-border p-6 max-h-[calc(100vh-100px)] overflow-y-auto">
        {!currentLesson && <div className="text-muted-foreground">Pick a lesson to start.</div>}
        {currentLesson && (
          <div className="space-y-4">
            <div>
              <h1 className="text-2xl font-semibold">{currentLesson.title}</h1>
              <div className="text-[10px] uppercase text-muted-foreground mt-1">Lesson · {currentLesson.duration_min || 5} min</div>
            </div>
            {currentLesson.video_url && (
              <div className="aspect-video w-full border border-border">
                <iframe src={ytEmbed(currentLesson.video_url)} className="w-full h-full" title="video" allowFullScreen />
              </div>
            )}
            <article className="prose prose-sm max-w-none dark:prose-invert" data-testid="lms-lesson-body">
              {renderMd(currentLesson.body_md)}
            </article>
            <div className="flex items-center gap-2 pt-4 border-t border-border">
              {!currentLesson.completed ? (
                <Button onClick={() => complete(currentLesson.id)} disabled={busy} className="rounded-none" data-testid="lms-mark-complete">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Mark complete & continue
                </Button>
              ) : (
                <div className="text-sm text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Completed</div>
              )}
              <div className="flex-1" />
              <Button variant="outline" className="rounded-none" onClick={() => {
                const all = course.lessons || [];
                const i = all.findIndex((x) => x.id === currentLesson.id);
                if (i < all.length - 1) setCurrentLesson(all[i + 1]);
              }}>
                Next lesson <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
